import * as React from "react";
import {
    Box,
    Card,
    CardActionArea,
    Chip,
    Divider,
    Stack,
    Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

import selfieOs from "../assets/images/os-trich.jpg";
import blogCoverImg from "../assets/images/blog-header.jpg";
import sparrowxBlogImg from "../assets/images/orchestration/title.png";
import javaRenaissanceBlogImg from "../assets/images/java-duke.jpg";
import embabelImg from "../assets/images/java-embabel.jpg";
import informationTheoryImg from "../assets/images/information-theory.jpg";
import agiInventionImg from "../assets/images/oxford-union.jpg"
import {Helmet} from "react-helmet-async";
import {useNavigate} from "react-router-dom";

type Post = {
    id: string;
    title: string;
    titleStrike?: string;
    subtitle: string;
    date: string;
    readTime: string;
    memberOnly?: boolean;
    image?: string;
    tags?: string[];
    link?: string;
};

const posts: Post[] = [
    {
        id: "embabelVlangchain",
        title: "Embabel or LangGraph ?(And Why Companies failed to replace engineers with AI)",
        subtitle:
            "How Increasing Workload Complexity, Not Smarter Tools, Revealed the Irreplaceable Role of Humans.",
        date: "Feb 19, 2026",
        readTime: "7 min read",
        memberOnly: false,
        image: embabelImg,
        tags: [
            "Artificial Intelligence", "Complexity Theory", "Automation", "Decision Theory",
            "Software Engineering", "Socio-Technical Systems", "AI Engineering"
        ],
        link: "/blog/autonomy-architecture"
    },
    {
        id: "javaRenaissance",
        title: "The Java Renaissance: Why the “Rewrite in Rust” Era is Coming to an End",
        subtitle:
            "How Loom, Panama, Valhalla, Leyden and a decade of hard lessons dismantled the real reasons companies abandoned Java.",
        date: "Jan 5, 2026",
        readTime: "6 min read",
        memberOnly: false,
        image:
            javaRenaissanceBlogImg,
        tags: [
            "Java",
            "Cloud Architecture",
            "Distributed Systems",
            "Microservices",
            "Performance Engineering",
            "OpenJDK",
            "Software Infrastructure"
        ],
        link: "/blog/java-renaissance"
    },
    {
        id: "information-theory",
        title: "When Information Learned to Think",
        subtitle:
            "Information theory’s journey from West Africa's talking drums(700AD) and telegraphs(1800s) to bits, logic, AI, and the human brain(21st Century).",
        date: "Jun 20, 2025",
        readTime: "10 min read",
        memberOnly: true,
        image: informationTheoryImg,
        tags: ["Information Theory", "Shannon Entropy", "Cybernetics", "Codes & Compression", "Robotics",
            "History of Computing", "Philosophy of Information"],

        link: "/blog/information-theory"
    },
    {
        id: "agi-invention",
        title: "Will AGI Be Humanity’s Last Great Invention?",
        subtitle:
            "A reflection on the Oxford Union debate exploring whether AGI will end human invention: Not by " +
            "replacing us, but by reshaping the conditions that made invention meaningful.",
        date: "Mar 27, 2026",
        readTime: "8 min read",
        memberOnly: false,
        image: agiInventionImg,
        tags: [
            "AGI","Singularity","Philosophy","Moravec’s Paradox","Cognitive Architectures","AI Alignment & Ethics",
        ],
        link: "/blog/agi-invention"
    },
    {
        id: "sparrowx-durable-agents",
        title: "From Agent Loops to Durable Agentic Systems",
        subtitle:
            "A SparrowX case study on where agent frameworks end, durable orchestration begins, and how systems like " +
            "Embabel and Temporal work together in production.",
        date: "Sep 11, 2026",
        readTime: "12 min read",
        memberOnly: true,
        image: sparrowxBlogImg,
        tags: [
            "Agentic AI", "AI Agents", "Agent Orchestration", "Durable Execution", "Distributed Systems",
            "System Design", "Temporal", "Embabel", "Human-in-the-Loop", "AI Infrastructure"
        ],
        link: "/blog/agentic-architecture"
    }

];

function PostRow({ post }: { post: Post }) {
    return (
        <Box sx={{ p: 0 }}>
            <Stack
                direction={{ xs: "column-reverse", sm: "row" }}
                spacing={{ xs: 1.5, sm: 3 }}
                alignItems="stretch"
            >
                {/* Text */}
                <Box sx={{ flex: 1, minWidth: 0, p: { xs: 2, sm: 2 } }}>
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 800,
                            lineHeight: 1.2,
                            color: "#242424",
                            fontFamily:
                                "system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica, Arial, sans-serif;",
                            fontSize: { xs: "1.15rem", sm: "1.5rem" },
                        }}
                    >
                        {post.titleStrike && (
                            <Box
                                component="span"
                                sx={{
                                    textDecoration: "line-through",
                                    color: "text.secondary",
                                    mr: 1,
                                }}
                            >
                                {post.titleStrike}
                            </Box>
                        )}
                        {post.title}
                    </Typography>

                    <Typography
                        variant="body1"
                        sx={{
                            mt: 1,
                            color: "#6b6b6b",
                            lineHeight: 1.5,
                            fontSize: { xs: "0.95rem", sm: "1rem" },
                        }}
                    >
                        {post.subtitle}
                    </Typography>

                    {/* Meta row */}
                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{
                            mt: 2,
                            flexWrap: "wrap",
                            rowGap: 1,
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{ color: "text.secondary", whiteSpace: "nowrap" }}
                        >
                            {post.date} • {post.readTime}
                        </Typography>

                        {/* On desktop, keep tags on the same line; on mobile, push to next line */}
                        <Box sx={{ flexBasis: { xs: "100%", sm: "auto" } }} />

                        <Stack
                            direction="row"
                            useFlexGap
                            sx={{
                                flexWrap: "wrap",
                                gap: 1,
                                justifyContent: "flex-start",
                            }}
                        >
                            {(post.tags ?? []).map((t) => (
                                <Chip key={t} size="small" label={t} />
                            ))}
                        </Stack>
                    </Stack>
                </Box>

                {/* Image */}
                <Box
                    sx={{
                        width: { xs: "100%", sm: 160 },
                        height: { xs: 180, sm: 190 },
                        flexShrink: 0,
                        borderRadius: 2,
                        overflow: "hidden",
                        bgcolor: "grey.100",
                        border: "1px solid",
                        borderColor: "divider",
                        mx: { xs: 2, sm: 0 }, // breathing room on mobile
                        mt: { xs: 2, sm: 0 },
                    }}
                >
                    <Box
                        component="img"
                        src={post.image}
                        alt={post.title}
                        sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                </Box>
            </Stack>

            <Divider sx={{ mt: 3 }} />
        </Box>
    );
}

export default function BlogHomePage() {
    const navigate = useNavigate();


    React.useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }, []);

    const SIDE_W = 310;
    const TOP_STRIP_H = 56;

    return (
        <>
            <Helmet>
                <title>Aggrey Lelei Blog | Distributed Systems, AI & Backend Engineering</title>

                <meta
                    name="description"
                    content="Insights on distributed systems, microservices, AI autonomy, JVM innovations, and backend architecture by Aggrey Lelei. Explore deep technical essays and engineering perspectives."
                />

                <link rel="canonical" href="https://aggreylelei.com/blog/" />

                {/* Open Graph (for LinkedIn, Twitter previews) */}
                <meta property="og:title" content="Aggrey Lelei Blog" />
                <meta
                    property="og:description"
                    content="Deep dives into distributed systems, AI agentic architectures, JVM evolution, and backend engineering."
                />
                <meta property="og:url" content="https://aggreylelei.com/blog/" />
                <meta property="og:type" content="website" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
            </Helmet>
            <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
            {/* Top strip */}
            <Box
                sx={{
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                }}
            >
                <Box
                    sx={{
                        maxWidth: 1700,
                        mx: "auto",
                        px: { xs: 2, md: 3 },
                        py: 1.5,
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                    }}
                >
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        email • your@email.com
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        medium / github • your-handle
                    </Typography>
                </Box>
            </Box>

            {/* LEFT content area */}
            <Box
                sx={{
                    maxWidth: 1400,
                    mx: "auto",
                    pl: { xs: 4, md: 4 },
                    pt: { xs: 1, md: 2 },
                    pb: 8,
                    pr: { md: `${SIDE_W }px` }
                }}
            >
                {/* small left gutter like the screenshot */}
                <Box sx={{ pl: { xs: 0, md: 0 } }}>
                    {/* Header area */}
                    <Box sx={{ maxWidth: 1400 }}>
                        {/* cover / hero */}
                        <Box
                            sx={{
                                paddingLeft: -30,
                                height: 130,
                                border: "1px solid",
                                borderColor: "divider",
                                objectFit: "cover",
                                color: "#cfc0a9",
                                backgroundImage: `
                  linear-gradient(
                    to left,
                    #d8c6b2 20%,
                    #ede6dc 100%
                  ),
                  linear-gradient(
                    to right,
                    #936e54 20%,
                    #bfa694 100%
                  ),
                  url(${blogCoverImg})
                `,
                                backgroundSize: `31.333% 100%,31.333% 100%,37.333% 100%`,
                                backgroundPosition: `left center,right center, center center`,
                                backgroundRepeat: "no-repeat",
                                display: "flex",
                                alignItems: "flex-end",
                                justifyContent: "space-between",
                                px: 2,
                                pb: 1.5,
                            }}
                        ></Box>

                        <Divider sx={{ mt: 1 }} />
                    </Box>

                    {/* Posts list (narrower than header) */}
                    {/* ✅ centered on the header's middle */}
                    <Box
                        sx={{
                            mt: 3,
                            maxWidth: 790,
                            mx: "auto",
                            //px: { xs: 0, sm: 0 },
                        }}
                    >
                        {/* Post List Mapping */}
                        <Stack spacing={3}>
                            {posts.map((p) => (
                                <Card key={p.id} elevation={0}
                                      sx={{
                                          borderRadius: 2,
                                          border: "1px solid",
                                          borderColor: "divider",
                                          overflow: "hidden",
                                        }}>
                                    <CardActionArea
                                        component={RouterLink}
                                        to={p.link}
                                        sx={{ borderRadius: 2 }}
                                    >
                                        <PostRow post={p} />
                                    </CardActionArea>
                                </Card>
                            ))}
                        </Stack>
                    </Box>
                </Box>
            </Box>

            {/* RIGHT column */}
            <Box
                sx={{
                    display: { xs: "none", md: "block" },
                    position: "fixed",
                    right: 0,
                    top: 0,
                    height: "100vh",
                    width: `${SIDE_W}px`,
                    bgcolor: "background.paper",
                    borderLeft: "1px solid",
                    borderColor: "divider",
                    zIndex: 10,
                }}
            >
                <Box sx={{ height: TOP_STRIP_H }} />

                <Box
                    sx={{
                        px: 3,
                        py: 3,
                        height: `calc(100vh - ${TOP_STRIP_H}px)`,
                        overflow: "auto",
                    }}
                >
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Box
                            sx={{
                                width: 120,
                                height: 120,
                                borderRadius: "50%",
                                overflow: "hidden",
                                flexShrink: 0,
                            }}
                        >
                            <Box
                                component="a"
                                href={selfieOs}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    display: "block",
                                    width: "100%",
                                    height: "100%",
                                    cursor: "pointer",
                                }}
                            >
                                <Box
                                    component="img"
                                    src={selfieOs}
                                    alt="Profile"
                                    sx={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                        display: "block",
                                    }}
                                />
                            </Box>
                        </Box>
                    </Stack>

                    <Stack spacing={1.25} sx={{ mt: 2 }}>
                        <Typography variant="body2">
                            I write about Distributed Systems, Scalability, JVM performance,
                            Observability and production-grade backend engineering, with a focus
                            on Microservices, Event-Driven Architecture and Agentic AI Systems.
                            <br/>
                            <br/>
                        </Typography>

                        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
                            <Chip size="small" label="Spring Boot" />
                            <Chip size="small" label="AWS" />
                            <Chip size="small" label="LLMs" />
                            <Chip size="small" label="Kafka" />
                            <Chip size="small" label="System Design" />
                            <Chip size="small" label="Information Theory" />

                        </Stack>
                        <br/>
                        <Divider sx={{ my: 1.5 }} />



                    </Stack>
                </Box>
            </Box>

            {/* Mobile fallback */}
            <Box sx={{ display: { xs: "block", md: "none" },  pb: 6 }}>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                    About
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Building reliable backend systems. Writing about JVM performance,
                    observability, and production lessons.
                </Typography>
            </Box>
        </Box>
        </>
    );
}
